import { client, gql } from 'data-provider';
import { ITransferStock } from 'interfaces';

export interface ITransferStockQuery {
  startDate?: string;
  endDate?: string;
  searchString?: string;
  page?: number;
  limit?: number;
}

export const getTransferStocks = async (transferStockQuery?: ITransferStockQuery) => {
  const where = {
    query:
      transferStockQuery && transferStockQuery.searchString ? transferStockQuery.searchString : '',
    startDate:
      transferStockQuery && transferStockQuery.startDate ? transferStockQuery.startDate : undefined,
    endDate:
      transferStockQuery && transferStockQuery.endDate ? transferStockQuery.endDate : undefined,
  };

  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      transferStocksCopy(sort: $sort, where: $where, start: $start, limit: $limit) {
        total
        size
        page
        data {
          id
          transferDate
          transferStatus
          fromWarehouse {
            name
            warehouseCode
          }
          toWarehouse {
            name
            warehouseCode
          }
          totalStockValue
          createdBy {
            name
          }
        }
      }
    }
  `;

  const variables = {
    where,
    start: transferStockQuery?.page ?? 1,
    limit: transferStockQuery?.limit ?? 10,
  };

  const data = await client.request(query, variables);
  return data.transferStocksCopy as {
    data: ITransferStock[];
    page: number;
    size: number;
    total: number;
  };
};

export interface CreateTransferStockData {
  fromWarehouse: string;
  toWarehouse: string;
  note: string | null;
  transferDate: string;
  transferStatus: string;
  transferStockProducts: {
    quantity: number;
    unitPrice: number;
    productVariant: string;
  }[];
  transactionToken: string;
}

export const createTransferStock = async (data: CreateTransferStockData) => {
  const mutation = gql`
    mutation ($input: createTransferStockCopyInput!) {
      createTransferStockCopy(input: $input) {
        id
        transferStatus
        fromWarehouse {
          id
          name
        }
        toWarehouse {
          id
          name
        }
      }
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(mutation, variables);
  return createdData;
};

export const getTransferStocksDropdown = async (startDate?: string, endDate?: string) => {
  const where = { startDate, endDate };

  const query = gql`
    query ($where: JSON) {
      transferStockDropdown(where: $where) {
        id
        transferDate
        fromWarehouse {
          name
          warehouseCode
        }
        toWarehouse {
          name
          warehouseCode
        }
        totalStockValue
        createdBy {
          name
        }
      }
    }
  `;

  const variables = { where };

  const data = await client.request(query, variables);
  return data.transferStockDropdown as ITransferStock[];
};

export const getTransferStock = async (id: string) => {
  const query = gql`
    query ($id: ID!) {
      transferStockCopy(id: $id) {
        id
        transferDate
        totalStockValue
        transferStatus
        note
        rejectionReason
        approvedAt
        fromWarehouse {
          name
        }
        toWarehouse {
          name
        }
        createdBy {
          name
        }
        approvedBy {
          name
        }
        transferStockProductsCopy {
          id
          productVariant {
            id
            sku
            name
            productCopy {
              id
              name
            }
          }
          quantity
          unitPrice
          stockValue
        }
      }
    }
  `;

  const variables = { id };

  const data = await client.request(query, variables);
  return data.transferStockCopy as ITransferStock;
};

export const getTransferStockInvoice = async (id: string) => {
  const query = gql`
    query Query($generateTransferStockInvoicePdfId: ID!, $timeZone: String!) {
      generateTransferStockInvoicePdf(id: $generateTransferStockInvoicePdfId, timeZone: $timeZone)
    }
  `;

  const variables = {
    generateTransferStockInvoicePdfId: id,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  const data = await client.request(query, variables);
  return data.generateTransferStockInvoicePdf as string;
};

export const approveTransferStock = async (id: string) => {
  const mutation = gql`
    mutation ($input: ApproveTransferStockInput!) {
      approveTransferStock(input: $input) {
        transferStockCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: { where: { id } },
  };

  return client.request(mutation, variables);
};

export const rejectTransferStock = async (id: string, rejectionReason: string) => {
  const mutation = gql`
    mutation ($input: RejectTransferStockInput!) {
      rejectTransferStock(input: $input) {
        transferStockCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: { where: { id }, data: { rejectionReason } },
  };

  return client.request(mutation, variables);
};

export const updateTransferStatus = async (id: string, transferStatus: string) => {
  const updateTransferStatus = gql`
    mutation ($input: updateTransferStockStatusInput!) {
      updateTransferStockStatus(input: $input) {
        transferStockCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
      data: { transferStatus },
    },
  };
  const updatedData = await client.request(updateTransferStatus, variables);
  return updatedData;
};
